// Stored media paths (the 2026-08-13 standard): the database keeps a
// WORKSPACE-RELATIVE path — 'assets/x.webp', 'catalog/2/photo.webp' — never a
// host. Anything that needs to DISPLAY one turns it into a fetchable address
// here. In-app we use the shared host with the workspace NUMBER, which every
// environment serves (locally the shop name only exists in the local database,
// so the number is the only form that resolves). Published pages get theirs
// from the backend's urlService instead — this helper is for the app UI only.
const STORED_MEDIA_PREFIX = '(?:assets|videos|catalog|collections|thumbnails|audio)/'
const STORED_MEDIA_RE = new RegExp('^' + STORED_MEDIA_PREFIX)
// Shared template previews retain their full storage key because their owner
// is workspace-templates, not the workspace currently open in the editor.
const STORAGE_MEDIA_KEY_RE = new RegExp(`^/?workspaces/([^/]+/${STORED_MEDIA_PREFIX}.+)$`)
const HTML_STORED_MEDIA_ATTR_RE = new RegExp(
  `(\\b(?:src|poster|data-pj-hls-src)\\s*=\\s*)(["']?)(${STORED_MEDIA_PREFIX}[^"'\\s>]+)\\2`,
  'gi',
)

export function resolveMediaUrl(
  src: string | null | undefined,
  workspaceId: number | string | null | undefined,
): string {
  if (!src) return ''
  const storagePath = STORAGE_MEDIA_KEY_RE.exec(src)?.[1]
  if (!storagePath && !STORED_MEDIA_RE.test(src)) return src
  const base = String(import.meta.env.VITE_ON_URL || '').replace(/\/+$/, '')
  if (!base) return src
  if (storagePath) return `${base}/${storagePath}`
  if (workspaceId == null || workspaceId === '') return src
  return `${base}/${workspaceId}/${src}`
}

/**
 * CORS mode for a displayed media address. ProfitJam's own hosts (the sites
 * host and the CDN) answer with Access-Control-Allow-Origin, and only a
 * CORS-mode request lets a canvas read the pixels afterwards: the instant
 * design cover draws each video's frame into an image, and a frame from a
 * plain request taints that image and the capture fails. Anyone else's file is
 * left as a plain request so it still loads when its host sends no CORS.
 */
export function mediaCrossOrigin(url: string | null | undefined): 'anonymous' | undefined {
  if (!url || !/^https?:\/\//i.test(url)) return undefined
  const ownHosts = [import.meta.env.VITE_ON_URL, import.meta.env.VITE_CDN_URL]
    .map((base) => { try { return new URL(String(base || '')).host } catch { return '' } })
    .filter(Boolean)
  try { return ownHosts.includes(new URL(url).host) ? 'anonymous' : undefined } catch { return undefined }
}

// `url(assets/x.webp)` inside authored CSS, quoted or not. Only stored-path
// forms are captured — absolute/data:/blob: urls never match.
const CSS_URL_STORED_RE = new RegExp(
  `url\\(\\s*(['"]?)(${STORED_MEDIA_PREFIX}[^'")]+)\\1\\s*\\)`,
  'g',
)

/** Rewrite every stored-media `url(...)` in a CSS string to a fetchable
 *  address via resolveMediaUrl. Display-time only — the stored CSS keeps the
 *  workspace-relative path (published pages are served from the site root,
 *  where the relative form already resolves). */
export function resolveMediaUrlsInCss(
  css: string,
  workspaceId: number | string | null | undefined,
): string {
  if (!css || workspaceId == null || workspaceId === '') return css
  return css.replace(CSS_URL_STORED_RE, (_m, quote: string, path: string) =>
    `url(${quote}${resolveMediaUrl(path, workspaceId)}${quote})`)
}

/** Resolve stored media paths inside compiled rich-text HTML for in-app
 * previews. This changes only src/poster/HLS attributes at display time; the
 * Blog revision and CollectionRecord keep their hostless stored paths. */
export function resolveMediaUrlsInHtml(
  html: string,
  workspaceId: number | string | null | undefined,
): string {
  if (!html || workspaceId == null || workspaceId === '') return html
  return html.replace(
    HTML_STORED_MEDIA_ATTR_RE,
    (_match, prefix: string, quote: string, stored: string) =>
      `${prefix}${quote}${resolveMediaUrl(stored, workspaceId)}${quote}`,
  )
}
